import {
    type AddressCreateRequestDto,
    Country,
    EnergyDataQualityLevel,
} from "@/api";
import type { AxiosResponse } from "axios";
import { clsx, type ClassValue } from "clsx";
import { Fragment } from "react/jsx-runtime";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";
import i18next from "i18next";
import { getFaro } from "@/observability/faro";
import { LogLevel } from "@grafana/faro-web-sdk";

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function ensureAddress(dto: {
    address?: AddressCreateRequestDto | null;
}): AddressCreateRequestDto {
    if (!dto.address) {
        dto.address = {
            street: null,
            zipCode: null,
            city: null,
            country: Country.Austria,
        };
    }
    return dto.address;
}

// Shape of ResultControllerBase error bodies; not part of the OpenAPI spec.
type ApiErrorResponse = { message: string };

export function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
    if (typeof data !== "object" || data === null) {
        return false;
    }
    return typeof (data as Record<string, unknown>).message === "string";
}

const MOCK_ENTITY_ID = -1;

export function isRealDocument(
    document: { id: number } | null | undefined,
): boolean {
    return document != null && document.id !== MOCK_ENTITY_ID;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
    return typeof value === "object" && value !== null
        ? (value as Record<string, unknown>)
        : undefined;
}

function getErrorResponse(err: unknown) {
    const response = asRecord(asRecord(err)?.response);
    const status = response?.status;
    const contentType = asRecord(response?.headers)?.["content-type"];
    return {
        exists: response != null,
        status: typeof status === "number" ? status : undefined,
        contentType:
            typeof contentType === "string" ||
            typeof contentType === "number" ||
            Array.isArray(contentType)
                ? String(contentType).toLowerCase()
                : "",
        data: response?.data,
    };
}

export async function extractErrorMessage(err: unknown): Promise<string> {
    const response = getErrorResponse(err);
    const statusCode = response.status ?? 500;
    const contentType = response.contentType;
    const isHtmlResponse = contentType.includes("text/html");

    const extractText = (raw: string): string => {
        const trimmed = raw.trim();
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
            try {
                const parsed: unknown = JSON.parse(trimmed);
                return isApiErrorResponse(parsed)
                    ? (parsed.message ?? raw)
                    : raw;
            } catch {
                return raw;
            }
        }
        return raw;
    };

    let errorText = "";
    if (isHtmlResponse) {
        errorText = i18next.t("common.service-unavailable");
    } else if (response.data) {
        const data = response.data;
        if (data instanceof Blob) {
            try {
                errorText = extractText(await data.text());
            } catch {
                errorText = i18next.t("common.error-reading-response");
            }
        } else if (isApiErrorResponse(data)) {
            errorText = data.message;
        } else if (typeof data === "string") {
            errorText = extractText(data);
        } else {
            errorText = i18next.t("common.default-error");
        }
    }

    if (!errorText.trim()) {
        errorText =
            statusCode === 403
                ? i18next.t("common.insufficient-permissions")
                : i18next.t("common.default-error");
    }

    return errorText;
}

// Axios reports a connectivity failure with no `response` at all: the request
// was cancelled, the tab was backgrounded, or the network dropped. These are
// not server faults, so they must not be reported as 5xx.
function getErrorCode(err: unknown): string | undefined {
    const code = asRecord(err)?.code;
    return typeof code === "string" ? code : undefined;
}

function isConnectivityError(err: unknown): boolean {
    const code = getErrorCode(err);
    return (
        !getErrorResponse(err).exists &&
        (code === "ERR_NETWORK" ||
            code === "ECONNABORTED" ||
            code === "ETIMEDOUT" ||
            code === "ERR_CANCELED")
    );
}

export async function handleErrorToast(err: unknown) {
    if (asRecord(err)?.suppressToast) {
        return;
    }

    if (isConnectivityError(err)) {
        // A cancelled request is the expected result of navigating away — no
        // toast, and nothing worth reporting.
        if (getErrorCode(err) !== "ERR_CANCELED") {
            toast.warning(i18next.t("common.network-error"));
            getFaro()?.api.pushLog(
                [`API unreachable: ${getErrorCode(err) ?? "unknown"}`],
                {
                    level: LogLevel.WARN,
                    context: { url: window.location.pathname },
                },
            );
        }
        return;
    }

    const statusCode = getErrorResponse(err).status ?? 500;
    const errorText = await extractErrorMessage(err);

    const errorMessage = errorText.split("\n").map((line, index, array) => (
        <Fragment key={index}>
            {line}
            {index < array.length - 1 ? <br /> : null}
        </Fragment>
    ));

    const faro = getFaro();
    const reportContext = {
        statusCode: String(statusCode),
        url: window.location.pathname,
        apiType: getErrorCode(err) ?? "",
    };

    if (statusCode >= 400 && statusCode < 500) {
        toast.warning(errorMessage);
        faro?.api.pushLog([`API ${statusCode}: ${errorText}`], {
            level: LogLevel.WARN,
            context: reportContext,
        });
    } else if (statusCode >= 500) {
        toast.error(errorMessage);
        faro?.api.pushError(err instanceof Error ? err : new Error(errorText), {
            context: reportContext,
            fingerprint: `api ${statusCode} ${reportContext.url}`,
        });
    } else {
        toast.info(errorMessage);
    }
}

export async function nukeClientState(): Promise<void> {
    const tasks: Promise<unknown>[] = [];
    if ("serviceWorker" in navigator) {
        tasks.push(
            navigator.serviceWorker
                .getRegistrations()
                .then((regs) => Promise.all(regs.map((r) => r.unregister())))
                .catch(() => undefined),
        );
    }
    if ("caches" in self) {
        tasks.push(
            caches
                .keys()
                .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
                .catch(() => undefined),
        );
    }
    await Promise.all(tasks);
}

export function debounce<A extends unknown[]>(
    func: (...args: A) => unknown,
    wait: number,
): (...args: A) => void {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    return (...args: A) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
}

export async function handleFileDownload<R extends AxiosResponse<Blob>>({
    apiCall,
    defaultFilename,
    onSuccess,
    onError,
}: {
    apiCall: () => Promise<R>;
    defaultFilename: string;
    onSuccess?: () => void;
    onError?: (error: unknown) => void | Promise<void>;
}): Promise<R> {
    try {
        const response = await apiCall();

        const rawDisposition: unknown = response.headers["content-disposition"];
        const contentDisposition =
            typeof rawDisposition === "string" ? rawDisposition : undefined;
        let filename = defaultFilename;

        if (contentDisposition) {
            const filenameStarMatch = contentDisposition.match(
                /filename\*=[^']*'[^']*'([^;\n]*)/i,
            );
            if (filenameStarMatch?.[1]) {
                try {
                    filename = decodeURIComponent(filenameStarMatch[1].trim());
                } catch {
                    filename = filenameStarMatch[1].trim();
                }
            } else {
                const filenameMatch = contentDisposition.match(
                    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
                );
                if (filenameMatch?.[1]) {
                    filename = filenameMatch[1].replace(/['"]/g, "");
                }
            }
        }

        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();

        if (onSuccess) onSuccess();
        return response;
    } catch (error: unknown) {
        if (onError) await onError(error);
        throw error;
    }
}

export function hslToHex(h: number, s: number, l: number): string {
    let r, g, b;

    if (s === 0) {
        r = g = b = l; // achromatic
    } else {
        const hue2rgb = (p: number, q: number, t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };

        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }

    const toHex = (x: number) => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function generateColorShades(
    count: number,
    baseColorHex: string,
    hueRange: number = 0.15,
): string[] {
    // Convert base color to HSL for easier manipulation
    const r = parseInt(baseColorHex.slice(1, 3), 16) / 255;
    const g = parseInt(baseColorHex.slice(3, 5), 16) / 255;
    const b = parseInt(baseColorHex.slice(5, 7), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
        s;
    const l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }

    const colors: string[] = [];

    for (let i = 0; i < count; i++) {
        // Calculate variations based on position in sequence
        const ratio = count > 1 ? i / (count - 1) : 0;

        // Wider hue variation to make colors more distinct
        const newHue = h + (ratio - 0.5) * hueRange;

        // Keep saturation high for vibrant colors
        const newSaturation = Math.max(0.8, Math.min(0.95, s * 1.3));

        // Create a wider lightness range for better contrast
        const newLightness = Math.max(0.3, Math.min(0.7, 0.65 - ratio * 0.35));

        // Convert HSL to hex
        colors.push(hslToHex(newHue, newSaturation, newLightness));
    }

    return colors;
}

export function getLowerQualityValue(
    val: EnergyDataQualityLevel,
    compVal: EnergyDataQualityLevel,
): EnergyDataQualityLevel {
    if (
        val === EnergyDataQualityLevel.L3 ||
        compVal === EnergyDataQualityLevel.L3
    )
        return EnergyDataQualityLevel.L3;
    if (
        val === EnergyDataQualityLevel.L2 ||
        compVal === EnergyDataQualityLevel.L2
    )
        return EnergyDataQualityLevel.L2;
    return EnergyDataQualityLevel.L1;
}

const CONSUMPTION_COLORS = [
    "#30BB65", // Bright green
    "#389155", // Dark green
    "#2D7847", // Forest green
    "#235F39", // Deep green
    "#19482C", // Very dark green
    "#10331E", // Almost black green
    "#4ADE80", // Light green
    "#22C55E", // Emerald
    "#16A34A", // Green 600
    "#15803D", // Green 700
    "#166534", // Green 800
    "#14532D", // Green 900
];

const PRODUCTION_COLORS = [
    "#9CC3E9", // Light blue
    "#83A9CD", // Soft blue
    "#6A8FB3", // Medium blue
    "#53799C", // Slate blue
    "#3E6486", // Deep blue
    "#2A5272", // Dark blue
    "#60A5FA", // Blue 400
    "#3B82F6", // Blue 500
    "#2563EB", // Blue 600
    "#1D4ED8", // Blue 700
    "#1E40AF", // Blue 800
    "#1E3A8A", // Blue 900
];

const GREEN_BASE_HUE = 0.38;
const BLUE_BASE_HUE = 0.58;

function generateUniqueColor(
    index: number,
    baseHue: number,
    usedColors: Set<string>,
): string {
    const offset = index - 12;
    const hueVariation = ((offset * 0.02) % 0.1) - 0.05;
    const hue = baseHue + hueVariation;
    const saturation = 0.65 + (offset % 3) * 0.1;
    const lightness = 0.35 + ((offset * 7) % 5) * 0.08;

    let color = hslToHex(hue, saturation, lightness);

    let attempts = 0;
    while (usedColors.has(color) && attempts < 50) {
        const adjustedLightness =
            lightness + (attempts % 2 === 0 ? 0.05 : -0.05) * (attempts + 1);
        const adjustedSaturation =
            saturation + (attempts % 3 === 0 ? 0.05 : -0.03) * (attempts + 1);
        color = hslToHex(
            hue,
            Math.max(0.3, Math.min(0.95, adjustedSaturation)),
            Math.max(0.25, Math.min(0.75, adjustedLightness)),
        );
        attempts++;
    }

    return color;
}

export type ChartColorMaps = {
    production: Record<string, string>;
    consumption: Record<string, string>;
};

export function generateChartColorMaps(
    names: string[],
    gridName: string,
    gridProductionColor: string,
    gridConsumptionColor: string,
): ChartColorMaps {
    const productionMap: Record<string, string> = {};
    const consumptionMap: Record<string, string> = {};

    const usedProductionColors = new Set<string>([gridProductionColor]);
    const usedConsumptionColors = new Set<string>([gridConsumptionColor]);

    const sortedNames = [...names].filter((n) => n !== gridName).sort();

    sortedNames.forEach((name, index) => {
        const productionColor =
            PRODUCTION_COLORS[index] ??
            generateUniqueColor(index, BLUE_BASE_HUE, usedProductionColors);
        const consumptionColor =
            CONSUMPTION_COLORS[index] ??
            generateUniqueColor(index, GREEN_BASE_HUE, usedConsumptionColors);

        usedProductionColors.add(productionColor);
        usedConsumptionColors.add(consumptionColor);

        productionMap[name] = productionColor;
        consumptionMap[name] = consumptionColor;
    });

    productionMap[gridName] = gridProductionColor;
    consumptionMap[gridName] = gridConsumptionColor;

    return {
        production: productionMap,
        consumption: consumptionMap,
    };
}

export function formatCurrency(amount: number, locale = "de-AT"): string {
    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: "EUR",
    }).format(amount);
}
